/**
 * The tuning scenario — the three equations of the balance pass, with every
 * constant in them exposed as a parameter.
 *
 *   evolution   E = (M / S) ^ p              S = mutagen at 100%, p = evo power
 *   passive     P = a·E^n + b·E              per second
 *   instant     T = c·M                      linear in the mutagen sent
 *
 * This is not a third branch that exists somewhere — it is the shape the branch
 * is being tuned *into*, and the whole point is that it is a family rather than
 * one curve. `live` and `testing3v3` are transcriptions; this one is an input.
 *
 * Two segments, and a switch for whether there are two. `evoStopScalingAt100`
 * decides what happens past 100%:
 *
 *   true   the power law is replaced by its own tangent at 100%, so the price of
 *          an evo point freezes at S/p mutagen and never rises again. No kink:
 *          dE/dM is continuous across the join. This is what the branch ships.
 *   false  the power law simply keeps running, charging more per point forever.
 *
 * The switch also moves the passive exponent, which is the subtle part —
 * `effectivePassivePower` below explains why.
 */

/* -------------------------------------------------------------------------- */
/*  DEFAULTS — the numbers to edit                                             */
/* -------------------------------------------------------------------------- */

/**
 * Where the sliders start, and what "Reset scenario" returns to. Everything else
 * in this file is machinery; this block is the knob box.
 *
 * These are the *candidate* being tuned, not what the game ships. The game's own
 * copy is `maps/biter_battles_v2/feeding_params.lua`, and it moves only when a
 * candidate is chosen — so the two are expected to differ while a pass is in
 * progress, and the lab is the side that runs ahead.
 */
export const DEFAULT_SCENARIO_PARAMS: ScenarioParams = {
  // evolution:  E = (M / evoMutagenAt100) ^ evoPower
  evoMutagenAt100: 277,
  evoPower: 0.3,
  evoStopScalingAt100: true,

  // passive threat per second:  passiveScale · E^passivePower + passiveLinear · E
  passiveScale: 100,
  passivePower: 2.5,
  passiveLinear: 25,

  // instant threat:  instantScale · M
  instantScale: 200,
};

/* -------------------------------------------------------------------------- */

export interface ScenarioParams {
  /** `S` — mutagen that buys exactly 100% evolution. */
  evoMutagenAt100: number;
  /** `p` in `E = (M / S) ^ p`. Below 1 it is the diminishing return. */
  evoPower: number;
  /** True: past 100% the curve follows its own tangent. False: the power law runs on. */
  evoStopScalingAt100: boolean;
  /** `a` — coefficient of the powered term of passive income. */
  passiveScale: number;
  /** `n` — exponent of that term, *before* the past-100% correction. */
  passivePower: number;
  /** `b` — coefficient of the linear term, which is the floor at low evolution. */
  passiveLinear: number;
  /** `c` — instant threat per unit of mutagen sent. */
  instantScale: number;
}

/* -------------------------------------------------------------------------- */
/*  BOUNDS — where the family is defined                                       */
/* -------------------------------------------------------------------------- */

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/**
 * `p` is the diminishing return itself, so the family only means anything
 * strictly inside `(0, 1)`: at 1 evolution is linear in mutagen and the price of
 * a point never moves, and above it every flask buys more than the last.
 *
 * The reachable ends are one step short of 0 and 1, which is what an open
 * interval amounts to on a control with a step.
 */
export const EVO_POWER_STEP = 0.01;
export const EVO_POWER_MIN = EVO_POWER_STEP;
export const EVO_POWER_MAX = 1 - EVO_POWER_STEP;

export const PASSIVE_POWER_STEP = 0.01;

/**
 * The window `n` lives in, which is `(1, 1/p)` and therefore moves with `p`.
 *
 * Below 1 the powered term bends *downward* and the linear term `b·E` is left
 * doing all the work — there is no reason to carry `a·E^n` at all. At `1/p` the
 * exponent in mutagen reaches 1: under the tangent `E^n` grows like
 * `(M/S)^(n·p)`, so `n·p = 1` is where passive income stops diminishing in the
 * mutagen fed, and past it income per flask climbs without limit.
 *
 * The margin is a quarter of the window rather than a fixed step, so the bounds
 * stay ordered — and the field stays usable — as `p` approaches 1 and the window
 * closes to almost nothing.
 */
export function passivePowerBounds(evoPower: number): { min: number; max: number } {
  const ceiling = 1 / clamp(evoPower, EVO_POWER_MIN, EVO_POWER_MAX);
  const margin = Math.min(PASSIVE_POWER_STEP, (ceiling - 1) / 4);
  return { min: 1 + margin, max: ceiling - margin };
}

/**
 * Put a parameter set inside those bounds.
 *
 * Every write goes through here rather than only the ones that look risky:
 * `n`'s window depends on `p`, so raising `p` far enough has to drag `n` down
 * with it instead of leaving the pair somewhere no curve is defined.
 */
export function clampScenario(params: ScenarioParams): ScenarioParams {
  const evoPower = clamp(params.evoPower, EVO_POWER_MIN, EVO_POWER_MAX);
  const n = passivePowerBounds(evoPower);
  return {
    evoMutagenAt100: Math.max(params.evoMutagenAt100, 1e-6),
    evoPower,
    evoStopScalingAt100: params.evoStopScalingAt100,
    passiveScale: Math.max(params.passiveScale, 0),
    passivePower: clamp(params.passivePower, n.min, n.max),
    passiveLinear: Math.max(params.passiveLinear, 0),
    instantScale: Math.max(params.instantScale, 0),
  };
}

/**
 * The exponent passive income actually runs at.
 *
 * With the tangent on (`evoStopScalingAt100`), evolution is eventually linear in
 * mutagen, so `E^n` is already a degree-`n` polynomial in mutagen and `n` needs
 * no correction.
 *
 * With it off, evolution stays `(M/S)^p` forever — and since `p < 1`, `E^n`
 * grows like `M^(n·p)`, which for the default n = 2.5, p = 0.3 is `M^0.75`:
 * *sublinear*, so income per flask would fall away rather than climb. Dividing
 * the exponent by `p` cancels that exactly — `E^(n/p) ≡ (M/S)^n` — so the
 * powered term scales as `M^n` under either switch position and "n = 2.5" means
 * the same thing to a player in both.
 */
export function effectivePassivePower(params: ScenarioParams): number {
  if (params.evoStopScalingAt100) return params.passivePower;
  return params.evoPower > 0 ? params.passivePower / params.evoPower : params.passivePower;
}

/** `dE/dM` on the linear segment: the tangent slope of the power law at 100%. */
export function scenarioLinearRate(params: ScenarioParams): number {
  return params.evoPower / params.evoMutagenAt100;
}

/* ----------------------------------------------------------------- evolution */

/** Evolution reached by feeding `mutagen` from zero. */
export function scenarioEvoForMutagen(mutagen: number, params: ScenarioParams): number {
  if (!(mutagen > 0)) return 0;
  const scale = params.evoMutagenAt100;
  if (params.evoStopScalingAt100 && mutagen > scale) {
    return 1 + (mutagen - scale) * scenarioLinearRate(params);
  }
  return (mutagen / scale) ** params.evoPower;
}

/** Total mutagen behind an evolution level. Exact inverse of the above. */
export function scenarioMutagenForEvo(evo: number, params: ScenarioParams): number {
  if (!(evo > 0)) return 0;
  const scale = params.evoMutagenAt100;
  if (params.evoStopScalingAt100 && evo > 1) {
    return scale + (evo - 1) / scenarioLinearRate(params);
  }
  return evo ** (1 / params.evoPower) * scale;
}

/**
 * `dE/dM`, as a function of evolution rather than of mutagen.
 *
 * Written this way because every caller already has the evolution in hand, and
 * because it is exact: `M/S = E^(1/p)` substitutes straight into
 * `dE/dM = (p/S)·(M/S)^(p-1)` to give `(p/S)·E^(1 - 1/p)`.
 *
 * Diverges as evolution approaches zero — the first sliver of evolution is
 * almost free — which is real, not a defect, and is why the ratio below starts
 * at zero rather than at some finite value.
 */
export function scenarioEvoSlope(evo: number, params: ScenarioParams): number {
  const rate = scenarioLinearRate(params);
  if (params.evoStopScalingAt100 && evo > 1) return rate;
  if (!(evo > 0)) return Infinity;
  return rate * evo ** (1 - 1 / params.evoPower);
}

/* ------------------------------------------------------------------- passive */

/** Passive threat income per second at `evo`. */
export function scenarioPassivePerSecond(evo: number, params: ScenarioParams): number {
  const e = Math.max(evo, 0);
  return params.passiveScale * e ** effectivePassivePower(params) + params.passiveLinear * e;
}

/** `dP/dE` — how much income one point of evolution is worth, per second. */
export function scenarioPassiveSlopePerEvo(evo: number, params: ScenarioParams): number {
  const e = Math.max(evo, 0);
  const n = effectivePassivePower(params);
  const powered = e > 0 ? params.passiveScale * n * e ** (n - 1) : n > 1 ? 0 : Infinity;
  return powered + params.passiveLinear;
}

/** `dP/dM` — income per second bought by one more unit of mutagen. */
export function scenarioPassiveSlope(evo: number, params: ScenarioParams): number {
  return scenarioPassiveSlopePerEvo(evo, params) * scenarioEvoSlope(evo, params);
}

/**
 * Evolution at which income reaches `perSecond`, by bisection.
 *
 * `a·E^n + b·E` inverts in closed form only for particular n, and n is a slider
 * here, so there is nothing to special-case. The function is strictly
 * increasing wherever a, b >= 0, which makes the search exact.
 */
export function scenarioEvoForPassive(perSecond: number, params: ScenarioParams): number {
  if (!(perSecond > 0)) return 0;
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 200 && scenarioPassivePerSecond(hi, params) < perSecond; i += 1) hi *= 2;
  for (let i = 0; i < 80; i += 1) {
    const mid = (lo + hi) / 2;
    if (scenarioPassivePerSecond(mid, params) < perSecond) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

/* ------------------------------------------------------------------ identity */

/**
 * A stable key for one parameter set, so anything cached per branch stays
 * correct when a slider moves. Every field is in it, in a fixed order.
 */
export function scenarioKey(params: ScenarioParams): string {
  return [
    params.evoMutagenAt100,
    params.evoPower,
    params.evoStopScalingAt100 ? 1 : 0,
    params.passiveScale,
    params.passivePower,
    params.passiveLinear,
    params.instantScale,
  ].join(':');
}

/** Round-trip for the address bar, so a tuned view can be linked to. */
export function encodeScenario(params: ScenarioParams): string {
  return scenarioKey(params).replace(/:/g, ',');
}

/**
 * Parse `encodeScenario`'s output. Every field falls back to its default
 * independently: a link written against an older field order degrades to a
 * partly-default scenario rather than to NaN curves. A link written before the
 * bounds existed degrades the same way, through `clampScenario`.
 */
export function decodeScenario(text: string | null | undefined): ScenarioParams {
  if (!text) return DEFAULT_SCENARIO_PARAMS;
  const parts = text.split(',').map((part) => Number(part.trim()));
  const at = (index: number, fallback: number): number =>
    Number.isFinite(parts[index]) ? parts[index] : fallback;
  const d = DEFAULT_SCENARIO_PARAMS;
  return clampScenario({
    evoMutagenAt100: at(0, d.evoMutagenAt100),
    evoPower: at(1, d.evoPower),
    evoStopScalingAt100: Number.isFinite(parts[2]) ? parts[2] !== 0 : d.evoStopScalingAt100,
    passiveScale: at(3, d.passiveScale),
    passivePower: at(4, d.passivePower),
    passiveLinear: at(5, d.passiveLinear),
    instantScale: at(6, d.instantScale),
  });
}

export function isDefaultScenario(params: ScenarioParams): boolean {
  return scenarioKey(params) === scenarioKey(DEFAULT_SCENARIO_PARAMS);
}
